/** Users API paths. Base URL (colocaApiUrl) already includes /api/v1. */
export const USERS_API_ENDPOINTS = {
  USERS: "/internal/users",
  USERS_COUNTS: "/internal/users/counts",
  userById: (id: string) => `/internal/users/${id}`,
  verifyEmail: (id: string) => `/internal/users/${id}/verify-email`,
} as const;

/** Matches the access screen so the two users tables page identically. */
export const USERS_PAGE_SIZE = 25;

/**
 * The API's own floor and ceiling for `search` (`@MinLength(3) @MaxLength(100)`). Below three characters the
 * term is not sent at all — the backend's trigram index needs three, and sending fewer is a 400 rather than a
 * broader match. Mirrored from `ACCESS_SEARCH_*` so the two surfaces cannot drift.
 */
export const USERS_SEARCH_MIN_LENGTH = 3;
export const USERS_SEARCH_MAX_LENGTH = 100;
export const USERS_SEARCH_DEBOUNCE_MS = 350;

/**
 * Sentinel for "no filter". It must never reach the wire.
 *
 * `?status=` is a 400 — not because of `forbidNonWhitelisted` (`status` IS declared on the DTO) but because
 * `@IsOptional()` skips `null`/`undefined` and NOT the empty string, so `@IsEnum` then rejects it. Same
 * outcome, different reason; worth stating precisely so nobody "fixes" it by declaring another param.
 */
export const USERS_FILTER_ALL = "all";

/**
 * The three locales the platform ships, as the API's `preferred_language` accepts them.
 *
 * Labels are endonyms — a language's own name needs no translation, and rendering "Spanish" to a Spanish
 * speaker choosing their language would be worse than useless. Declared once because two forms offer it.
 */
export const USER_LANGUAGE_OPTIONS = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
] as const;

export const USER_LANGUAGES = ["es", "en", "pt"] as const;

export type UserLanguage = (typeof USER_LANGUAGES)[number];
